import type {
  IAoeCart,
  IAoeCartEarnedDiscount,
  IHostRewardSummaryItem,
} from '../types/CartTypes'
import {
  DiscountAwardType,
  HostRewardSummaryKind,
  getDiscountAwardTypeLabelKey,
  getHostRewardSummaryKind,
} from '../constants/DiscountAwardType'

/**
 * Builds the Host rewards summary ("what has the Host earned, and what is left
 * to redeem") from the cart's earned discounts. Mirrors the Prowess
 * "Rabatte und Sonderangebote" summary box.
 *
 * Field semantics come from the OrderEntry award gateways
 * (DiscountPlanAwardGateway.CreateOrUpdateOrderDiscount):
 * - FreeDollars:  totalAmount = host dollars earned, appliedAmount = host dollars
 *                 used on products. Order-level usage (FreeDollarsOffOrder +
 *                 free-dollar-cost coupons) comes from GetOffOrderHostDollarsUsed
 * - Product count awards (PercentageOffItem, DiscountedSkuFromList,
 *   SelectFreeProduct): totalAmount = number earned, quantity = number still
 *   unselected
 * - DiscountedItem: a single product; redeemed once a cart item is tied to it
 */
export function BuildHostRewardsSummary(
  cart: IAoeCart | null | undefined,
): IHostRewardSummaryItem[] {
  if (!cart || !Array.isArray(cart.earnedDiscounts)) return []

  const seenIds = new Set<number>()
  const summary: IHostRewardSummaryItem[] = []
  // Host dollars spent on the whole order / on coupons. The backend attributes
  // this to the first FreeDollars award (FreeDollarsOffOrderGateway uses
  // OrderDiscounts.FirstOrDefault), so it is only added to that one.
  let offOrderHostDollarsUsed: number | null = GetOffOrderHostDollarsUsed(cart)

  for (const earnedDiscount of cart.earnedDiscounts) {
    if (seenIds.has(earnedDiscount.poeDiscPlanLevelDiscId)) continue
    seenIds.add(earnedDiscount.poeDiscPlanLevelDiscId)

    if (IsHostDollarSpend(earnedDiscount)) continue

    const kind = getHostRewardSummaryKind(earnedDiscount.systemKeyword)
    let earned: number | null = null
    let remaining: number | null = null

    if (kind === HostRewardSummaryKind.CURRENCY) {
      earned = ToNumber(earnedDiscount.totalAmount)
      if (earned <= 0) continue
      let used = ToNumber(earnedDiscount.appliedAmount)
      if (offOrderHostDollarsUsed != null) {
        used = Math.max(used, offOrderHostDollarsUsed)
        offOrderHostDollarsUsed = null
      }
      remaining = Math.max(earned - Math.min(used, earned), 0)
    } else if (kind === HostRewardSummaryKind.COUNT) {
      if (earnedDiscount.systemKeyword === DiscountAwardType.DISCOUNTED_ITEM) {
        earned = 1
        remaining = IsTiedToCartItem(cart, earnedDiscount) ? 0 : 1
      } else {
        earned = ToNumber(earnedDiscount.totalAmount)
        const unselected =
          earnedDiscount.quantity != null
            ? ToNumber(earnedDiscount.quantity)
            : earned - ToNumber(earnedDiscount.appliedAmount)
        remaining = Math.min(Math.max(unselected, 0), earned)
      }
      if (earned <= 0) continue
    }

    summary.push({
      poeDiscPlanLevelDiscId: earnedDiscount.poeDiscPlanLevelDiscId,
      systemKeyword: earnedDiscount.systemKeyword,
      kind,
      labelKey: getDiscountAwardTypeLabelKey(earnedDiscount.systemKeyword),
      detail:
        ToPlainText(earnedDiscount.awardMessage) ||
        ToPlainText(earnedDiscount.awardDescription),
      earned,
      remaining,
    })
  }

  return summary
}

/**
 * Host dollars used when they are applied to the whole order
 * (FreeDollarsOffOrder) plus coupons bought with host dollars. Returns null
 * when the order-level mode is not in use.
 *
 * The FreeDollars award's own appliedAmount cannot be relied on for this:
 * FreeDollarsOffOrderGateway writes the order-level usage onto it, but
 * FreeDollarsGateway resets it to the item-level usage (normally 0 in this
 * mode) whenever it is evaluated afterwards, which depends on discount plan
 * configuration order. This mirrors what AoeCartDetailFreeDollars displays.
 */
function GetOffOrderHostDollarsUsed(cart: IAoeCart): number | null {
  const offOrder = cart.earnedDiscounts.find(
    ed => ed.systemKeyword === DiscountAwardType.FREE_DOLLARS_OFF_ORDER,
  )
  if (!offOrder) return null

  const couponCost = cart.earnedDiscounts
    .filter(
      ed =>
        ed.systemKeyword === DiscountAwardType.COUPON &&
        ed.isApplied &&
        ed.freeDollarsCost != null &&
        ed.freeDollarsCost > 0,
    )
    .reduce((sum, ed) => sum + ToNumber(ed.freeDollarsCost), 0)

  return ToNumber(offOrder.appliedAmount) + couponCost
}

/**
 * FreeDollarsOffOrder and coupons bought with host dollars are ways of
 * spending FreeDollars - their value is already counted in the FreeDollars
 * row's appliedAmount, so they are not listed separately (same rule as
 * AoeDiscountControl.FilterEarnedDisounts).
 */
function IsHostDollarSpend(earnedDiscount: IAoeCartEarnedDiscount): boolean {
  if (earnedDiscount.systemKeyword === DiscountAwardType.FREE_DOLLARS_OFF_ORDER)
    return true
  return (
    earnedDiscount.systemKeyword === DiscountAwardType.COUPON &&
    earnedDiscount.freeDollarsCost != null &&
    earnedDiscount.freeDollarsCost > 0
  )
}

function IsTiedToCartItem(
  cart: IAoeCart,
  earnedDiscount: IAoeCartEarnedDiscount,
): boolean {
  return (cart.items ?? []).some(item =>
    (item.poeDiscPlanLevelDiscIds ?? []).includes(
      earnedDiscount.poeDiscPlanLevelDiscId,
    ),
  )
}

function ToNumber(value: unknown): number {
  const n = Number(value)
  return isNaN(n) ? 0 : n
}

/**
 * awardMessage / awardDescription are admin-configured HTML. The summary shows
 * them as plain text, so tags are stripped rather than rendered with v-html.
 */
function ToPlainText(html: string | null | undefined): string {
  if (!html) return ''
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
